import type { AdminActivityEvent } from '@doneverse/shared'

/** The table's only sort (ClickUp `86d4cqpk1`). Absent means the server's own
 *  order: newest first, then by event id. */
export type DurationSort = 'duration_asc' | 'duration_desc'

export const DURATION_SORTS: readonly DurationSort[] = ['duration_asc', 'duration_desc']

/** One header click: time order -> ascending -> descending -> time order. */
export function nextDurationSort(current: DurationSort | undefined): DurationSort | undefined {
  if (current === undefined) return 'duration_asc'
  if (current === 'duration_asc') return 'duration_desc'
  return undefined
}

/** Sorts on raw `duration_seconds`, the field `durationCell` renders, so an
 *  idle row sorts by exactly what its cell shows. `formatHumanDuration` never
 *  decreases as the seconds grow, so the raw order and the displayed order
 *  cannot disagree; two cells that both read "1h 15m" are ordered by the
 *  seconds the cell drops.
 *
 *  A null duration renders "—" and goes LAST in both directions. It means "not
 *  recorded", not zero seconds; treated as zero it would lead the ascending
 *  sort and bury the shortest real row under a screen of dashes.
 *
 *  Ties fall back to the server's order (`listActivityEventsForUser`:
 *  `occurred_at` desc, then `client_event_id` desc) in both directions, so the
 *  same rows always render in the same order. `occurred_at` is compared as an
 *  instant rather than as a string, because two offsets for one moment do not
 *  sort as strings.
 *
 *  Correct only over the WHOLE range. Sorting the pages the infinite query
 *  happens to hold shows the longest of those rows, not the longest overall,
 *  which is why the page loads every row before calling this. */
export function sortEventsByDuration(events: AdminActivityEvent[], sort: DurationSort): AdminActivityEvent[] {
  const direction = sort === 'duration_asc' ? 1 : -1
  const instants = new Map(events.map((event) => [event, Date.parse(event.occurred_at)]))
  return [...events].sort((a, b) => {
    const aDuration = a.duration_seconds
    const bDuration = b.duration_seconds
    if (aDuration !== bDuration) {
      if (aDuration === null) return 1
      if (bDuration === null) return -1
      return (aDuration - bDuration) * direction
    }
    const byTime = instants.get(b)! - instants.get(a)!
    if (byTime !== 0) return byTime
    if (a.client_event_id === b.client_event_id) return 0
    return a.client_event_id < b.client_event_id ? 1 : -1
  })
}
